import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    DropDownSelector,
    InputDinamycPlaceholder,
    Checkbox,
    TextareaDynamicPlaceholder,
    InputListArray
} from '@lk-gtcom/ecomlab-components';
import { SingleDatePicker } from '../SingleDatePicker/SingleDatePicker';
import { ReactComponent as GrabZone } from './img/bx-grid-vertical.svg';
import { ModalNewTaskList } from './ModalEditDynamicFormComponent/ModalEditDynamicFormComponent';
import { ButtonActionDrop } from '../Buttons/ButtonActionDrop/ButtonActionDrop';
import './DynamicFormComponent.scss';
import { debounce } from 'lodash';
import { TextPage } from '../TextPage/TextPage';
import { useResponseHandler } from '../../common/auth';
import customHeaders, { getSpace } from '../../common/headers';
import useGeneralStore from '../../store/general';
import { ButtonWithIcon } from '../Buttons/ButtonWithIcon/ButtonWithIcon';
import TimePicker from '../TimePicker/TimePicker';

import plus_icon from './img/plus.svg'
import minus_icon from './img/minus.svg'
import done_icon from './img/done.svg'

const getTooltip = (data_type) => {
    switch (data_type) {
        case 'str':
            return 'Строка';
        case 'string':
            return 'Строка';
        case 'int':
            return 'Целое число';
        case 'float':
            return 'Число с плавающей точкой';
        case 'array':
            return 'Список значений через запятую';
    }
}

const DISCOUNT_FIELDS = [
    { key: 'order_amount', placeholder: 'Сумма от' },
    { key: 'discount_value', placeholder: 'Скидка' },
];

const DiscountLevelInput = ({ value = [], onChange, disabled, placeholder }) => {
    const [rows, setRows] = useState(Array.isArray(value) ? value.map(v => ({ order_amount: String(v.order_amount ?? ''), discount_value: String(v.discount_value ?? '') })) : []);
    const [draft, setDraft] = useState({ order_amount: '', discount_value: '' });

    const notify = (nextRows) => onChange(nextRows.map(r => ({ order_amount: Number(r.order_amount), discount_value: Number(r.discount_value) })));

    const canAdd = (() => {
        const f = Number(draft.order_amount), d = Number(draft.discount_value);
        if (!draft.order_amount || !draft.discount_value || isNaN(f) || isNaN(d) || f <= 0 || d <= 0) return false;
        if (rows.length === 0) return true;
        return f > Number(rows[rows.length - 1].order_amount) && d > Number(rows[rows.length - 1].discount_value);
    })();

    const handleAdd = () => {
        if (!canAdd) return;
        const next = [...rows, { order_amount: draft.order_amount, discount_value: draft.discount_value }];
        setRows(next);
        setDraft({ order_amount: '', discount_value: '' });
        notify(next);
    };

    const handleRemove = (i) => {
        const next = rows.filter((_, idx) => idx !== i);
        setRows(next);
        notify(next);
    };

    const prev = rows.length > 0 ? rows[rows.length - 1] : null;

    return (
        <div className="discount-level-input">
            <TextPage text={placeholder} />
            {rows.map((row, i) => (
                <div key={i} className="discount-level-input__row">
                    {DISCOUNT_FIELDS.map(({ key, placeholder: ph }) => (
                        <div key={key} className="discount-level-input__field">
                            <InputDinamycPlaceholder
                                placeholder={ph}
                                changeValue={row[key]}
                                type="number"
                                size="s"
                                maxWidth="100%"
                                isClearable
                                disabled={disabled}
                                tooltipStatic={i > 0 ? `значение больше ${rows[i - 1][key]}` : undefined}
                                onBlur={(val) => {
                                    const next = rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r);
                                    setRows(next);
                                    notify(next);
                                }}
                            />
                        </div>
                    ))}
                    {!disabled &&
                        <ButtonWithIcon
                            icon={minus_icon}
                            red
                            size='m'
                            disabled={canAdd}
                            onClick={() => handleRemove(i)}
                        />
                    }
                </div>
            ))}
            {!disabled && (
                <div className="discount-level-input__row">
                    {DISCOUNT_FIELDS.map(({ key, placeholder: ph }) => (
                        <div key={key} className="discount-level-input__field">
                            <InputDinamycPlaceholder
                                placeholder={ph}
                                changeValue={draft[key]}
                                type="number"
                                size="s"
                                maxWidth="100%"
                                isClearable
                                tooltipStatic={prev ? `значение больше ${prev[key]}` : undefined}
                                onBlur={(val) => setDraft(d => ({ ...d, [key]: val }))}
                            />
                        </div>
                    ))}

                    <ButtonWithIcon
                        icon={done_icon}
                        green
                        size='m'
                        disabled={canAdd}
                        onClick={handleAdd}
                    />
                </div>
            )}
        </div>
    );
};

const DropDownAdapter = React.memo(({
    api_id,
    data,
    handleInputChange,
    fetchDictValues,
    fetchSetValue,
    errField,
    isEdit,
    readonly,
    selectedData,
    onFilterChange,
    isOpenUp = false
}) => {

    const {
        attribute_id,
        attribute_name,
        front_name,
        value,
        description,
        is_array,
        required,
        field_source
    } = data;

    // Поддержка данных с бэка без нормализации
    const id = data.id ?? attribute_id;

    const selectedSpace = useGeneralStore((state) => state.selectedSpace);
    const headers = { ...customHeaders, ...getSpace(selectedSpace) }
    const { handleTableResponse, handleRequestError } = useResponseHandler();;
    const options_list = data?.possible_values?.length ? data?.possible_values?.map((el) => ({ label: el, value: el })) : [];

    const [options, setOptions] = useState(options_list);
    const [selectedValue, setSelectedValue] = useState();
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);

    // Фильтры по ссылкам
    const [isUpdateFilter, setIsUpdateFilter] = useState(false)
    const [optionsData, setOptionsData] = useState([])
    const [localSelectedData, setLocalSelectedData] = useState(value);
    const [search, setSearch] = useState('')
    const [total, setTotal] = useState('')

    let filter_link = data?.filter_link

    const filters = Object.keys(selectedData)?.map((key) => {
        const value = selectedData[key];
        const data = Array.isArray(value) && value.length > 0 ? value.map((item) => item.value) : ['all'];

        return {
            id: key,
            data: data,
        };
    });

    const fetchFilters = (filter_id) => {
        if (!filters?.length) return
        const url = filter_link;

        let filtersWithoutLast = filters;

        const body = JSON.stringify({
            id: id,
            api_id: api_id,
            page: page,
            limit: 50,
            filters: filtersWithoutLast,
        });

        fetch(url, { body, method: 'POST', headers, })
            .then(handleTableResponse)
            .then((json) => {
                if (json) {
                    setTotal(json?.total)
                    const dataArr = json?.filter_data.map((el) => ({
                        label: el.name,
                        value: el.id,
                    }));
                    if (page > 1) {
                        setOptionsData((prev) => [...prev, ...dataArr]);
                    } else {
                        setOptionsData([...dataArr]);
                    }
                }
            })
            .catch(handleRequestError);
    };

    useEffect(() => {
        if (filter_link) {
            fetchFilters()
        }
    }, [page, search, selectedData]);

    useEffect(() => {
        if (!filter_link) {
            const abortSignal = new AbortController();
            const timeoutId = setTimeout(() => {
                fetchDictValues(page, query, setOptions, data, abortSignal.signal);
            }, 700);

            return () => {
                clearTimeout(timeoutId);
                abortSignal.abort();
            };
        }
    }, [query, page, data]);


    const onSelectHandler = useCallback((e) => {
        setSelectedValue(e);

        if (data.is_array) {
            // Для массива: если очистили (e === null или пустой массив), записываем пустой массив
            const currData = e ? e.map(el => el?.value) : [];
            handleInputChange(currData, data);
        } else {
            // Для одиночного значения: если очистили (e === null), записываем null
            const currValue = e ? e.value : null;
            handleInputChange(currValue, data);
        }
    }, [data, handleInputChange]);

    useEffect(() => {
        onFilterChange(id, localSelectedData);
    }, [isUpdateFilter]);

    const onMenuClose = () => {
        setTimeout(() => onFilterChange(id, localSelectedData), 10)
    }

    if (!data) return null;

    return (
        <div className='dynamic-form-component__drop-container'>
            <DropDownSelector
                width={filter_link ? '200%' : '100%'}
                maxWidth={filter_link ? '210px' : ''}
                placeholder={front_name ? front_name : attribute_name}
                options_prop={filter_link ? optionsData : options}
                queryValue={query}
                setInputChange={setQuery}
                fetchCallback={setPage}
                noUpwardPaging
                defaultValue={filter_link ? value :
                    (is_array ?
                        value?.length > 0 ? value?.map(el => ({ label: el?.toString(), value: el })) : []
                        :
                        value ? { label: value?.toString(), value: value } : null)
                }
                setState={(e) => {
                    if (filter_link) {
                        setLocalSelectedData(e)
                        handleInputChange(e, data)

                    } else {
                        fetchSetValue(id, is_array ? (e?.map(el => el.value) || []) : e?.value || null, field_source, front_name);
                        onSelectHandler(e);
                    }

                }}
                total={total}
                state={filter_link ? localSelectedData : selectedValue}
                multi={is_array}
                tooltip={description}
                err={required && errField && (is_array ? (selectedValue?.length < 1) : !selectedValue)}
                disabled={isEdit}
                isClearable={true}
                readonly={readonly}
                onMenuClose={onMenuClose}
                required={required}
                preventMenuUpward={isOpenUp}
            />
        </div>
    );
});

const DynamicFormComponent = ({
    api_id = null,
    productTemporary,
    setProductTemporary = () => { },
    fetchDictValues = () => { },
    fetchChange = false,
    fetchDelete = () => { },
    fetchSetValue = () => { },
    isEdit = false,
    checkbox = false,
    check = [],
    setCheck = () => { },
    draggable = false,
    readonly = false,
    isOpenUp = false
}) => {
    const [errField, setErrField] = useState(false);
    const [isModal, setIsModal] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    // Для фильтров бренд, категория и тд
    const [selectedData, setSelectedData] = useState([]);

    // Состояние для хранения дат (исправляет ошибку ESLint)
    const [dateValues, setDateValues] = useState({});
    const handleFilterChange = (id, data) => {
        setSelectedData((prevValues) => ({
            ...prevValues,
            [id]: data,
        }));
    };

    // DnD
    const [draggableElement, setDraggableElement] = useState(null)
    const [isOver, setIsOver] = useState(false);
    const [dragOverId, setDragOverId] = useState(null)
    const [dragOverCounter, setDragOverCounter] = useState(false)

    // Вспомогательная функция для получения id
    const getItemId = useCallback((item) => item?.id ?? item?.attribute_id, []);

    // Инициализация дат при загрузке или изменении данных
    useEffect(() => {
        const initialDates = {};
        productTemporary?.forEach(item => {
            const itemKey = getItemId(item);
            if (item.data_type === 'date' && item.value) {
                try {
                    initialDates[itemKey] = new Date(item.value);
                } catch (error) {
                    initialDates[itemKey] = new Date();
                }
            }
        });
        setDateValues(initialDates);
    }, [productTemporary, getItemId]);

    // Установка даты при первом рендере, если у нее нет значения
    useEffect(() => {
        productTemporary?.forEach(item => {
            const itemKey = getItemId(item);
            if (item.data_type === 'date' && !item.value) {
                const currentDate = new Date();
                updateDateValue(itemKey, currentDate);
                handleInputChange(currentDate.toISOString(), item);
                fetchSetValue(itemKey, currentDate.toISOString(), item?.field_source, item?.front_name);
            }
        });
    }, []); // Пустой массив зависимостей, чтобы выполнить только один раз

    const updateDateValue = useCallback((id, newDate) => {
        setDateValues(prev => ({
            ...prev,
            [id]: newDate
        }));
    }, []);

    const dragOverHandler = useCallback((e) => {
        if (draggable) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
        }
    }, [draggable]);

    const onDragStart = useCallback((e, targetElement) => {
        if (!draggable) return;
        e.stopPropagation();
        setDraggableElement(targetElement);
        e.dataTransfer.setData("text/plain", e.target.id);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.classList.add('dragging');
    }, [draggable]);

    const onDrop = useCallback((e, targetId) => {
        if (!draggable) return;
        e.preventDefault();
        e.stopPropagation();

        const name = draggableElement?.front_name;
        const id = getItemId(draggableElement);
        const type = draggableElement?.data_type;
        const possible_values = draggableElement?.possible_values;

        // Находим элемент перед targetId, чтобы вставить перед ним
        const targetIndex = productTemporary.findIndex(item => getItemId(item) === targetId);
        let beforeId = null;

        if (targetIndex > 0) {
            // Если есть элемент перед целевым, берем его before
            beforeId = productTemporary[targetIndex].before;
        } else {
            // Если целевой элемент первый в списке, вставляем в начало
            beforeId = productTemporary[0].before;
        }

        if (id === beforeId) {
            return;
        } else {
            // Передаем before элемента, перед которым нужно вставить
            fetchChange(name, id, type, beforeId, possible_values);
        }

        setDragOverId(null);
        setDraggableElement(null);
        setDragOverCounter(0);

        const elements = document.querySelectorAll('.dragging');
        elements.forEach(el => el.classList.remove('dragging'));
    }, [draggable, draggableElement, fetchChange, productTemporary, getItemId]);

    const handleDragEnter = useCallback((id) => {
        setDragOverId(id);
        setDragOverCounter(prev => prev + 1);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOverCounter(prev => {
            const newCounter = prev - 1;
            if (newCounter <= 0) {
                setDragOverId(null);
                return 0;
            }
            return newCounter;
        });
    }, []);

    const btns = useMemo(() => [
        {
            btn: 'Удалить',
            func: (e) => {
                if (fetchDelete) {
                    fetchDelete(e);
                }
            },
        },
    ], [fetchDelete]);

    const handleInputChange = useCallback((newValue, attribute) => {
        setProductTemporary(prevState => {
            let finalValue;

            if (attribute.data_type === 'filter') {
                finalValue = newValue;
            } else if (attribute.data_type === 'array[discount_level_object]') {
                finalValue = Array.isArray(newValue) ? newValue : [];
            } else if (attribute.is_array) {
                finalValue = Array.isArray(newValue) ? newValue : [newValue];
            } else if (attribute.data_type === 'int' || attribute.data_type === 'float' || attribute.data_type === 'num') {
                finalValue = (newValue || newValue === 0) ? Number(newValue) : null;
                if (isNaN(finalValue)) finalValue = null;
            } else if (attribute.data_type === 'date') {
                finalValue = newValue instanceof Date ? newValue.toISOString() : newValue;
            } else if (attribute.data_type === 'time') {
                finalValue = newValue;
            } else {
                finalValue = newValue;
            }

            // Используем attribute_id как фоллбэк если id не задан (данные пришли с бэка без нормализации)
            const attributeKey = attribute.id ?? attribute.attribute_id;

            return prevState?.map(attr => {
                const attrKey = attr.id ?? attr.attribute_id;
                return attrKey === attributeKey
                    ? { ...attr, value: finalValue }
                    : attr;
            });
        });
    }, [setProductTemporary]);

    const debouncedHandleInputChange = useMemo(
        () => debounce(handleInputChange, 0),
        [handleInputChange]
    );

    const onCheck = useCallback((id) => {
        setCheck(prev =>
            prev?.includes(id)
                ? prev.filter((el) => el !== id)
                : [...prev, id]
        );
    }, [setCheck]);

    // Функция для обработки изменения даты
    const handleDateChange = useCallback((date, attribute) => {
        if (date) {
            const attrKey = attribute.id ?? attribute.attribute_id;
            updateDateValue(attrKey, date);
            const dateString = date.toISOString();
            debouncedHandleInputChange(dateString, attribute);
            fetchSetValue(attrKey, dateString, attribute.field_source);
        }
    }, [debouncedHandleInputChange, fetchSetValue, updateDateValue]);

    const renderField = useCallback((el, index) => {
        const {
            attribute_id,
            attribute_name,
            front_name,
            data_type,
            description,
            required,
            value,
            is_dict_value,
            is_array,
            field_source,
            before,
            possible_values
        } = el;

        // Поддержка данных с бэка без нормализации: id может отсутствовать, используем attribute_id
        const id = el.id ?? attribute_id;

        const handleFieldChange = (e) => {
            debouncedHandleInputChange(e, el);
            fetchSetValue(id, e?.toString() || null, field_source, front_name);
        };

        const isCurrentOver = dragOverId === id;

        const commonDragProps = draggable ? {
            draggable: true,
            onDragOver: dragOverHandler,
            onDragStart: (e) => onDragStart(e, el),
            onDrop: (e) => onDrop(e, id),
            onDragEnter: (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragEnter(id);
            },
            onDragLeave: (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragLeave();
            },
            onDragEnd: (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('dragging');
                setDragOverId(null);
                setDragOverCounter(0);
            }
        } : {};

        // Создаем пропсы для внутренних элементов, чтобы они также реагировали на drag
        const innerElementProps = draggable ? {
            onDragEnter: (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragEnter(id);
            },
            onDragLeave: (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragLeave();
            },
            onDragOver: dragOverHandler,
        } : {};

        if (data_type === 'array[discount_level_object]') {
            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone className='dynamic-form-component__drop-icon' />}
                    {checkbox && <div onClick={(e) => e.stopPropagation()} {...innerElementProps}>
                        <Checkbox value={check?.includes(id)} onChange={() => onCheck(id)} />
                    </div>}
                    <div className="dynamic-form-component__field-content" onClick={(e) => e.stopPropagation()} {...innerElementProps}>
                        <DiscountLevelInput
                            value={Array.isArray(value) ? value : []}
                            placeholder={front_name || attribute_name}
                            disabled={isEdit}
                            onChange={(nextValue) => {
                                handleInputChange(nextValue, el);
                                fetchSetValue(id, nextValue, field_source, front_name);
                            }}
                        />
                    </div>
                    {isEdit && <div onClick={(e) => e.stopPropagation()} {...innerElementProps}>
                        <ButtonActionDrop id={id} btns={[{ btn: 'Редактировать', func: () => { setCurrentField({ id, front_name, data_type, attribute_name, before }); setIsModal(true); } }, ...btns]} />
                    </div>}
                </div>
            );
        }

        if (data_type === 'date') {
            const currentDate = dateValues[id] || (value ? new Date(value) : new Date());

            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone
                        className='dynamic-form-component__drop-icon' />}

                    {checkbox && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <Checkbox
                            value={check?.includes(id)}
                            onChange={() => onCheck(id)}
                        />
                    </div>}

                    <div
                        className="dynamic-form-component__field-content"
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        {isEdit ?
                            <div className="date-display">
                                {currentDate.toLocaleDateString('ru-RU')}
                            </div>
                            :
                            <SingleDatePicker
                                required={required}
                                placeholder={attribute_name}
                                date={currentDate}
                                setDate={(date) => handleDateChange(date, el)}
                                style={{ maxWidth: '210', minWidth: 'unset' }}
                                time={true}
                            />
                        }
                    </div>

                    {isEdit && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <ButtonActionDrop id={id}
                            btns={[{
                                btn: 'Редактировать',
                                func: () => {
                                    setCurrentField({ id, front_name, data_type, attribute_name, before });
                                    setIsModal(true);
                                },
                            }, ...btns]} />
                    </div>}
                </div>
            );
        }

        if (data_type === 'time') {
            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone
                        className='dynamic-form-component__drop-icon' />}

                    {checkbox && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <Checkbox
                            value={check?.includes(id)}
                            onChange={() => onCheck(id)}
                        />
                    </div>}

                    <div
                        className="dynamic-form-component__field-content"
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        {isEdit ?
                            <div className="time-display">
                                {value || '00:00:00'}
                            </div>
                            :
                            <TimePicker
                                placeholder={front_name || attribute_name}
                                value={value || '00:00:00'}
                                disabled={isEdit}
                                readonly={readonly}
                                onChange={(timeStr) => {
                                    handleInputChange(timeStr, el);
                                    fetchSetValue(id, timeStr, field_source, front_name);
                                }}
                            />
                        }
                    </div>

                    {isEdit && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <ButtonActionDrop id={id}
                            btns={[{
                                btn: 'Редактировать',
                                func: () => {
                                    setCurrentField({ id, front_name, data_type, attribute_name, before });
                                    setIsModal(true);
                                },
                            }, ...btns]} />
                    </div>}
                </div>
            );
        }

        if (data_type === 'field') {
            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone
                        className='dynamic-form-component__drop-icon' />}

                    {checkbox && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <Checkbox
                            value={check?.includes(id)}
                            onChange={() => onCheck(id)}
                        />
                    </div>}

                    <div
                        className="dynamic-form-component__field-content"
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <TextareaDynamicPlaceholder
                            placeholder={front_name || attribute_name}
                            changeValue={value ? `${value?.toString()}` : ''}
                            showClearIndicator={true}
                            type='string'
                            tooltipStatic={description}
                            autoComplete={false}
                            size='s'
                            required={required}
                            err={required && errField && (!value || value?.length < 1)}
                            setErr={setErrField}
                            disabled={isEdit}
                            readonly={readonly}
                            onChange={handleFieldChange}
                        />
                    </div>

                    {isEdit && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <ButtonActionDrop id={id}
                            btns={[{
                                btn: 'Редактировать',
                                func: () => {
                                    setCurrentField({ id, front_name, data_type, attribute_name, before });
                                    setIsModal(true);
                                },
                            }, ...btns]} />
                    </div>}
                </div>
            );
        }

        if (is_dict_value || data_type === 'dict' || data_type === 'list' || data_type === 'filter') {
            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone
                        className='dynamic-form-component__drop-icon' />}

                    {checkbox && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <Checkbox
                            value={check?.includes(id)}
                            onChange={() => onCheck(id)}
                        />
                    </div>}

                    <div
                        className="dynamic-form-component__field-content"
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <DropDownAdapter
                            api_id={api_id}
                            key={id}
                            data={el}
                            selectedData={selectedData}
                            handleInputChange={handleInputChange}
                            fetchDictValues={fetchDictValues}
                            fetchSetValue={fetchSetValue}
                            errField={errField}
                            isEdit={isEdit}
                            readonly={readonly}
                            onFilterChange={handleFilterChange}
                            isOpenUp={isOpenUp}
                        />
                    </div>

                    {isEdit && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <ButtonActionDrop id={id}
                            btns={[{
                                btn: 'Редактировать',
                                func: () => {
                                    setCurrentField({ id, front_name, data_type, attribute_name, before, possible_values });
                                    setIsModal(true);
                                },
                            }, ...btns]} />
                    </div>}
                </div>
            );
        }

        if (data_type === 'bool') {
            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone
                        className='dynamic-form-component__drop-icon' />}

                    {checkbox && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <Checkbox
                            value={check?.includes(id)}
                            onChange={() => onCheck(id)}
                        />
                    </div>}

                    <div
                        className="dynamic-form-component__field-content"
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <label className='checkbox-content'>
                            <Checkbox
                                id={id}
                                value={value}
                                onChange={(e) => {
                                    handleInputChange(e, el);
                                    fetchSetValue(id, e, field_source, front_name);
                                }}
                                disabled={isEdit}
                            >
                                <>
                                    {front_name || attribute_name}
                                    <p className='text_description'>{description}</p>
                                </>
                            </Checkbox>
                        </label>
                    </div>

                    {isEdit && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <ButtonActionDrop id={id}
                            btns={[{
                                btn: 'Редактировать',
                                func: () => {
                                    setCurrentField({ id, front_name, data_type, attribute_name, before });
                                    setIsModal(true);
                                },
                            }, ...btns]} />
                    </div>}
                </div>
            );
        }

        if (is_array || data_type === 'array') {
            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone
                        className='dynamic-form-component__drop-icon' />}

                    {checkbox && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <Checkbox
                            value={check?.includes(id)}
                            onChange={() => onCheck(id)}
                        />
                    </div>}

                    <div
                        className="dynamic-form-component__field-content"
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <InputListArray
                            placeholder={front_name || attribute_name}
                            changeValue={value || []}
                            onChange={handleFieldChange}
                            err={required && errField && (!value || value?.length < 1)}
                            setErr={setErrField}
                            info={'Введите значения через ENTER'}
                            showClearIndicator={true}
                            tooltipStatic={draggable ? getTooltip(data_type) : description}
                            disabled={isEdit}
                            readonly={readonly}
                        />
                    </div>

                    {isEdit && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <ButtonActionDrop id={id}
                            btns={[{
                                btn: 'Редактировать',
                                func: () => {
                                    setCurrentField({ id, front_name, data_type, attribute_name, before });
                                    setIsModal(true);
                                },
                            }, ...btns]} />
                    </div>}
                </div>
            );
        }

        if (data_type === 'str' || data_type === 'string' || data_type === 'int' || data_type === 'float' || data_type === 'percent') {
            return (
                <div
                    className={`dynamic-form-component__row ${isCurrentOver ? "dynamic-form-component__highlight" : ""} ${draggable ? "dynamic-form-component__draggable" : ""}`}
                    key={id}
                    {...commonDragProps}
                >
                    {draggable && <GrabZone
                        className='dynamic-form-component__drop-icon' />}

                    {checkbox && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <Checkbox
                            value={check?.includes(id)}
                            onChange={() => onCheck(id)}
                        />
                    </div>}

                    <div
                        className="dynamic-form-component__field-content"
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <InputDinamycPlaceholder
                            placeholder={front_name || attribute_name}
                            changeValue={value ? `${value?.toString()}` : ''}
                             type={(((data_type === 'int') || (data_type === 'percent')) || (data_type === 'float')) ? 'number' : 'string'}
                            tooltipStatic={draggable ? getTooltip(data_type) : description}
                            autoComplete={false}
                            size='s'
                            required={required}
                            err={required && errField && (!value || value?.length < 1)}
                            setErr={setErrField}
                            info={data_type === 'float' ? 'Введите целое число или дробное число, через точку' :
                                (data_type === 'int' ? 'Введите целое число, без точки' : '')}
                            disabled={isEdit}
                            maxWidth='100%'
                            readonly={readonly}
                            isClearable={true}
                            onBlur={handleFieldChange}
                        />
                    </div>

                    {isEdit && <div
                        onClick={(e) => e.stopPropagation()}
                        {...innerElementProps}
                    >
                        <ButtonActionDrop id={id}
                            btns={[{
                                btn: 'Редактировать',
                                func: () => {
                                    setCurrentField({ id, front_name, data_type, attribute_name, before });
                                    setIsModal(true);
                                },
                            }, ...btns]} />
                    </div>}
                </div>
            );
        }

        return null;
    }, [checkbox, check, onCheck, handleInputChange, debouncedHandleInputChange, fetchDictValues, fetchSetValue, errField, isEdit, btns, dateValues, handleDateChange, dragOverId, draggable, dragOverHandler, onDragStart, onDrop, handleDragEnter, handleDragLeave, readonly, getItemId]);

    const renderFieldReadOnly = () => {
        const fields = productTemporary.map(({ front_name, value, data_type }) => {
            if (value) {
                return <div className='dynamic-form-component__row-readonly' key={front_name}>
                    <TextPage
                        text={front_name + ': '}
                        style={{ fontWeight: 600, color: 'rgb(24, 144, 255)' }}
                    />
                    <TextPage
                        text={data_type == "bool" ? (value ? 'Да' : 'Нет') : value}
                    />
                </div>
            }
            return null;
        }).filter(Boolean);

        return <div className='dynamic-form-component__content-readonly'>{fields}</div>
    }

    return (
        <>
            {isModal && (
                <ModalNewTaskList
                    setIsModal={setIsModal}
                    current={currentField}
                    fetchChange={fetchChange}
                />
            )}

            {(productTemporary?.length && draggable) ? <p className='dynamic-form-component__info'>
                Перетаскивайте поля, чтобы менять их порядок. Отпустите над любым полем — переместится выше него в списке.
            </p> : null}

            <div className="dynamic-form-component">
                {productTemporary?.length ?
                    (readonly ? renderFieldReadOnly() : productTemporary.map(renderField))
                    :
                    <div className="no-attributes">Нет доступных атрибутов</div>
                }
            </div>
        </>
    );
};

export { DynamicFormComponent };
